import { useEffect, useMemo, useRef, useState } from "react";
import { Star, Quote } from "lucide-react";
import api from "@/lib/api";

const SOURCE_STYLES = {
  Google:    { badge: "bg-[#4285F4]/12 text-[#4285F4] border-[#4285F4]/25", dot: "bg-[#4285F4]" },
  Clutch:    { badge: "bg-[#17313B]/15 text-[#17313B] dark:text-[#8ec9d6] border-[#17313B]/30", dot: "bg-[#e63946]" },
  LinkedIn:  { badge: "bg-[#0A66C2]/12 text-[#0A66C2] border-[#0A66C2]/25", dot: "bg-[#0A66C2]" },
  GoodFirms: { badge: "bg-[#F26521]/12 text-[#F26521] border-[#F26521]/25", dot: "bg-[#F26521]" },
  Default:   { badge: "bg-foreground/10 text-foreground border-foreground/20", dot: "bg-foreground/60" },
};

function initials(name = "") {
  return name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() || "").join("") || "?";
}

function ReviewCard({ r }) {
  const s = SOURCE_STYLES[r.source] || SOURCE_STYLES.Default;
  return (
    <div
      className="relative shrink-0 w-[320px] md:w-[380px] rounded-3xl border border-border/70 bg-card/80 backdrop-blur-xl p-6 md:p-7 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-500"
      data-testid={`review-card-${r.id}`}
    >
      <Quote className="absolute top-5 right-5 w-8 h-8 text-primary/15" />

      {/* rating + source */}
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-0.5">
          {Array.from({ length: 5 }).map((_, i) => (
            <Star
              key={i}
              className={`w-4 h-4 ${i < (r.rating || 5) ? "fill-yellow-400 text-yellow-400" : "text-foreground/20"}`}
            />
          ))}
        </div>
        <span className={`inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.18em] px-2 py-1 rounded-full border ${s.badge}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
          {r.source}
        </span>
      </div>

      {/* body */}
      <p className="text-sm md:text-[15px] leading-relaxed text-foreground/90 line-clamp-6">
        &ldquo;{r.text}&rdquo;
      </p>

      {/* author */}
      <div className="mt-5 pt-4 border-t border-border/60 flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary/30 to-secondary/30 grid place-items-center text-xs font-heading font-bold text-primary overflow-hidden">
          {r.avatar_url ? (
            <img src={r.avatar_url} alt={r.author_name} className="w-full h-full object-cover" />
          ) : (
            initials(r.author_name)
          )}
        </div>
        <div className="min-w-0">
          <div className="font-heading font-semibold text-sm truncate">{r.author_name}</div>
          <div className="text-[11px] text-muted-foreground truncate">
            {[r.author_role, r.country].filter(Boolean).join(" · ") || "Verified client"}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ReviewsScroller() {
  const [reviews, setReviews] = useState([]);
  const [paused, setPaused] = useState(false);
  const trackRef = useRef(null);
  const posRef = useRef(0);
  const rafRef = useRef(null);

  useEffect(() => {
    api.get("/reviews").then((r) => setReviews(r.data || [])).catch(() => setReviews([]));
  }, []);

  // Duplicate the list so the marquee can loop seamlessly
  const loop = useMemo(() => [...reviews, ...reviews], [reviews]);

  useEffect(() => {
    const el = trackRef.current;
    if (!el || reviews.length === 0) return;
    const speed = 0.5; // px per frame ~ 30px/s at 60fps
    const step = () => {
      if (!paused) {
        posRef.current -= speed;
        const half = el.scrollWidth / 2;
        if (Math.abs(posRef.current) >= half) posRef.current = 0;
        el.style.transform = `translate3d(${posRef.current}px, 0, 0)`;
      }
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [reviews, paused]);

  if (reviews.length === 0) return null;

  const avg = (reviews.reduce((a, r) => a + (r.rating || 5), 0) / reviews.length).toFixed(1);

  return (
    <section className="relative py-16 md:py-24 overflow-hidden" data-testid="reviews-scroller-section">
      {/* aurora */}
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute top-1/2 left-1/4 -translate-y-1/2 w-[420px] h-[420px] rounded-full bg-primary/8 blur-[120px]" />
        <div className="absolute top-1/3 right-1/4 w-[360px] h-[360px] rounded-full bg-secondary/8 blur-[110px]" />
      </div>

      <div className="max-w-7xl mx-auto px-6">
        {/* Heading */}
        <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-6 mb-10 md:mb-14">
          <div className="max-w-2xl">
            <div className="text-xs font-mono uppercase tracking-[0.25em] text-muted-foreground">Loved by teams worldwide</div>
            <h2 className="mt-4 font-heading font-bold text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.02]">
              What clients say <span className="text-primary">out loud.</span>
            </h2>
            <p className="mt-4 text-muted-foreground text-sm md:text-base max-w-xl">
              Verified reviews from founders, deans and directors across the UK, Pakistan, KSA, UAE and beyond — pulled straight from Google, Clutch and LinkedIn.
            </p>
          </div>

          <div className="flex items-center gap-4 shrink-0">
            <div className="text-right">
              <div className="flex items-center gap-1 justify-end">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <div className="mt-1 text-xs text-muted-foreground">
                <b className="text-foreground">{avg}</b> average · <b className="text-foreground">{reviews.length}+</b> verified reviews
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Marquee row — constrained to same max-width as other sections */}
      <div className="max-w-7xl mx-auto px-6">
        <div
          className="relative overflow-hidden rounded-3xl"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          data-testid="reviews-marquee"
        >
          {/* edge fades */}
          <div aria-hidden className="pointer-events-none absolute inset-y-0 left-0 w-16 md:w-32 bg-gradient-to-r from-background via-background/80 to-transparent z-10" />
          <div aria-hidden className="pointer-events-none absolute inset-y-0 right-0 w-16 md:w-32 bg-gradient-to-l from-background via-background/80 to-transparent z-10" />

          <div
            ref={trackRef}
            className="flex gap-5 md:gap-6 will-change-transform py-2"
            style={{ transform: "translate3d(0,0,0)" }}
          >
            {loop.map((r, i) => (
              <ReviewCard key={`${r.id}-${i}`} r={r} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
